import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { TableColorCellComponent } from '@core/components/table/table-color-cell/table-color-cell.component';
import { capitalizeFirstLetter } from '@core/utils/capitalize-first-letter.util';
import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { KingUserHistory } from '../interfaces/king-history.interface';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { TableButtonCellComponent } from '@core/components/table/table-button-cell/table-button-cell.component';
import {
  STATUSES_ALLOW_REFUND,
  STATUSES_ERROR,
} from '../constants/statuses.constants';
import { User } from '@core/interfaces/user.interface';

import { KingHistory } from '@old-app/king/interfaces/king.interface';
import { PlayerRowComponent } from '@core/components/player-row/player-row.component';
import { TablePercentCellComponent } from '@core/components/table/table-percent-cell/table-percent-cell.component';
import { UserHistoryService } from '../services/user-history.service';
import { ModalService } from '@features/modals/services/modal.service';
import { CostComponent } from '@core/components/cost/cost.component';

interface Props {
  user?: User;
  userHistoryService: UserHistoryService;
  modalService: ModalService;
}

export const kingHistoryTableConfig = ({
  user,
  modalService,
  userHistoryService,
}: Props): ColumnDef<KingUserHistory>[] => [
  {
    accessorFn: (row) => row.gameid,
    id: 'gameid',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'ID',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.value,
    id: 'value',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Amount',
      };
    },
    cell: (cell) => {
      const value = cell.getValue() as number;
      return flexRenderComponent(CostComponent, {
        inputs: {
          cost: value,
          balanceType: 'skins',
          gap: 4,
          color: cell.row.original.winner ? 'var(--green)' : 'var(--white)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.winner,
    id: 'details',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Details',
      };
    },
    cell: (cell) => {
      const { ...kingHistory } = cell.row.original;

      const transformKingUserHistory = (): Pick<
        KingHistory,
        'roundid' | 'gameid' | 'winner' | 'king' | 'part'
      > => {
        return {
          roundid: kingHistory.round,
          gameid: kingHistory.gameid,
          king: {
            ...kingHistory.king,
            perc: 1,
            skins: userHistoryService.getSkinsByHistorySkins(
              kingHistory.king.skins
            ),
          },
          part: {
            ...kingHistory.opponent,
            perc: 1,
            skins: userHistoryService.getSkinsByHistorySkins(
              kingHistory.opponent.skins
            ),
          },
          winner: kingHistory.winner
            ? {
                ...kingHistory.king,
                perc: 1,
              }
            : {
                ...kingHistory.opponent,
                perc: 1,
              },
        };
      };

      const kingGame = transformKingUserHistory();

      return kingGame.king.skins.length || kingGame.part?.skins?.length
        ? flexRenderComponent(TableButtonCellComponent, {
            inputs: {
              text: 'View Items',
            },
            outputs: {
              handleClick: () => {
                modalService.openModal('KingSkinsModal', {
                  kingGame,
                  skins: [
                    kingGame.king.skins,
                    kingGame.part?.skins ?? [],
                  ].flat(),
                  totalValue: kingHistory.total_value,
                });
              },
            },
          })
        : '-';
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.status,
    id: 'status',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Status',
      };
    },
    cell: (cell) => {
      const kingGame = cell.row.original;

      const isResend = STATUSES_ALLOW_REFUND.includes(kingGame.status);

      if (isResend) {
        return flexRenderComponent(TableButtonCellComponent, {
          inputs: {
            text: 'Resend',
          },
          outputs: {
            handleClick: () =>
              userHistoryService.resendTrade(
                kingGame.gameid,
                kingGame.round,
                'king'
              ),
          },
        });
      } else {
        const getStatusText = () => {
          if (
            kingGame.winner ||
            (kingGame.king_fled > 0 && kingGame.king.steamid === user?.steamid)
          ) {
            if (
              kingGame.status === 'accepted' ||
              kingGame.status === 'active' ||
              kingGame.status === 'sent'
            ) {
              return {
                text: capitalizeFirstLetter(kingGame.status),
                color: 'var(--green)',
              };
            }

            if (kingGame.status === 'refunded') {
              return {
                text: 'Refunded',
                color: 'var(--yellow)',
              };
            }

            if (STATUSES_ERROR.includes(kingGame.status)) {
              return {
                text: `Error: ${kingGame.status}`,
                color: 'var(--red)',
              };
            }
          }
          return {
            text: 'No Winnings',
          };
        };

        const { text, color } = getStatusText();

        return flexRenderComponent(TableColorCellComponent, {
          inputs: {
            value: text,
            color: color,
          },
        });
      }
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.round,
    id: 'round',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Round',
      };
    },
    cell: (cell) => cell.getValue(),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.king_fled,
    id: 'king_fled',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'King Fled',
      };
    },
    cell: (cell) => (cell.getValue() ? 'Yes' : 'No'),
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.opponent.name,
    id: 'opponent',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Opponent',
      };
    },
    cell: (cell) => {
      const opponent = cell.row.original.opponent;
      const isNoOpponent = opponent.steamid === '';

      return isNoOpponent
        ? 'No opponent'
        : flexRenderComponent(PlayerRowComponent, {
            inputs: {
              player: { ...opponent },
            },
          });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.opponent.percentage,
    id: 'opponentPercentage',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Opponent Percentage',
      };
    },
    cell: (cell) => {
      return flexRenderComponent(TablePercentCellComponent, {
        inputs: {
          value: `${cell.row.original.opponent.percentage}%`,
          disabledFormat: true,
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.winner,
    id: 'winner',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Results',
      };
    },
    cell: (cell) => {
      const value = cell.getValue() as boolean;
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: value ? 'Won' : 'Lost',
          color: value ? 'var(--green)' : 'var(--red)',
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => row.ended,
    id: 'ended',
    header: ({ column }) => {
      return {
        onClick: () => column.toggleSorting(),
        content: 'Date',
      };
    },
    cell: (cell) => {
      const value = cell.row.original.ended;
      if (value === '0000-00-00 00:00:00') {
        return flexRenderComponent(TableColorCellComponent, {
          inputs: {
            value: value,
          },
        });
      }

      return flexRenderComponent(TableDateCellComponent, {
        inputs: {
          value,
        },
      });
    },
    enableSorting: true,
  },
  {
    id: 'fairness',
    header: () => 'Fairness',
    cell: (cell) =>
      flexRenderComponent(FairnessButtonComponent, {
        inputs: {
          href: `/provably-fair/king/${cell.row.original.gameid}/${cell.row.original.round}`,
        },
        outputs: {
          handleClick: () => {
            modalService.close();
          },
        },
      }),
    enableSorting: false,
  },
];
